/* After10thGroups.jsx — 5-group chooser landing page.
 *
 * Lives at /career-clarity/after-10th. Shows the 5 official Intermediate
 * groups (MPC, BiPC, MEC, CEC, HEC) as separate cards. Tapping a card
 * opens its self-contained 18-question quiz.
 *
 * STRICTLY independent of the existing After-Inter (B.Tech / BBA-MBA)
 * flow — no shared state, no shared route prefix beyond /career-clarity.
 */
import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { motion } from 'framer-motion';
import { Loader2, ArrowRight, ChevronLeft, Atom, FlaskConical, LineChart, Scale, BookOpen } from 'lucide-react';
import Navbar from '../components/Navbar';

const API = process.env.REACT_APP_BACKEND_URL;

// Visual treatment per group — gradient icon, soft chip, and a tinted body
const GROUP_VISUALS = {
  MPC:  { Icon: Atom,         gradient: 'from-blue-500 to-indigo-600',     soft: 'from-blue-50 to-indigo-50',    chip: 'bg-blue-100 text-blue-800',       glow: 'shadow-blue-200/50' },
  BiPC: { Icon: FlaskConical, gradient: 'from-emerald-500 to-green-600',   soft: 'from-emerald-50 to-green-50',  chip: 'bg-emerald-100 text-emerald-800', glow: 'shadow-emerald-200/50' },
  MEC:  { Icon: LineChart,    gradient: 'from-violet-500 to-fuchsia-600',  soft: 'from-violet-50 to-fuchsia-50', chip: 'bg-violet-100 text-violet-800',   glow: 'shadow-violet-200/50' },
  CEC:  { Icon: Scale,        gradient: 'from-amber-500 to-orange-600',    soft: 'from-amber-50 to-orange-50',   chip: 'bg-amber-100 text-amber-900',     glow: 'shadow-amber-200/50' },
  HEC:  { Icon: BookOpen,     gradient: 'from-rose-500 to-pink-600',       soft: 'from-rose-50 to-pink-50',      chip: 'bg-rose-100 text-rose-800',       glow: 'shadow-rose-200/50' },
};

export default function After10thGroups() {
  const [groups, setGroups] = useState(null);
  const [attempts, setAttempts] = useState({});  // per group: {attempts_used, attempts_remaining}
  const [maxAttempts, setMaxAttempts] = useState(2);
  const [isAdmin, setIsAdmin] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const [gr, at] = await Promise.all([
          axios.get(`${API}/api/after-10th/groups`),
          axios.get(`${API}/api/after-10th/attempts`, { withCredentials: true }).catch(() => null),
        ]);
        if (cancelled) return;
        setGroups(gr.data.groups);
        setMaxAttempts(gr.data.max_attempts || 2);
        if (at?.data?.by_group) setAttempts(at.data.by_group);
        if (at?.data?.is_admin) setIsAdmin(true);
      } catch (e) {
        if (!cancelled) setError('Could not load the groups. Refresh to try again.');
      }
    })();
    return () => { cancelled = true; };
  }, []);

  if (error) {
    return (
      <div className="min-h-screen bg-[#FFF9F1]">
        <Navbar/>
        <div className="pt-28 text-center text-red-600" data-testid="after10th-error">{error}</div>
      </div>
    );
  }

  if (!groups) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#FFF9F1]">
        <Loader2 className="w-8 h-8 animate-spin text-teal-600"/>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-amber-50 via-orange-50 to-rose-50" data-testid="after10th-groups">
      <Navbar/>
      <div className="pt-32 md:pt-36 pb-16 px-4 md:px-6">
        <div className="max-w-6xl mx-auto">

          <Link to="/career-clarity" className="inline-flex items-center gap-1 text-sm text-gray-500 hover:text-teal-700 mb-4" data-testid="after10th-back">
            <ChevronLeft size={16}/> Back to Career Clarity
          </Link>

          <div className="mb-10 relative">
            <div className="absolute -top-6 -left-6 w-32 h-32 rounded-full bg-gradient-to-br from-amber-200 to-rose-200 opacity-40 blur-2xl"/>
            <div className="absolute -top-4 right-0 w-40 h-40 rounded-full bg-gradient-to-br from-violet-200 to-pink-200 opacity-40 blur-3xl"/>
            <p className="text-[11px] font-extrabold uppercase tracking-[0.18em] text-transparent bg-clip-text bg-gradient-to-r from-amber-700 to-rose-700 mb-2 relative">
              Career Clarity · After 10th
            </p>
            <h1 className="text-3xl md:text-5xl font-extrabold text-gray-900 leading-tight tracking-tight relative" data-testid="after10th-headline">
              Just finished 10th? <span className="bg-gradient-to-r from-amber-600 to-rose-600 bg-clip-text text-transparent">Pick a group</span> to check the fit.
            </h1>
            <p className="text-gray-600 text-sm md:text-base mt-4 max-w-2xl leading-relaxed relative">
              Five official Intermediate groups, five honest quizzes. Open any
              one — answer 18 quick questions one at a time — get a fair report
              on whether the group really fits you. You can try as many groups
              as you like; <span className="font-semibold">max {maxAttempts} attempts per quiz</span>.
            </p>
          </div>

          {/* Study, Skills & Career Clarity guide + exploration quiz (v2 content) */}
          <Link to="/career-clarity/after-10th/guide" data-testid="after10th-guide-cta"
            className="group mb-8 flex items-center justify-between gap-3 bg-white/80 backdrop-blur border-2 border-amber-200 rounded-2xl px-5 py-4 hover:border-amber-400 hover:shadow-md transition-all">
            <span className="flex items-start gap-3 min-w-0">
              <span className="inline-flex w-10 h-10 shrink-0 rounded-xl bg-gradient-to-br from-amber-400 to-orange-500 text-white items-center justify-center"><BookOpen size={18}/></span>
              <span className="text-left min-w-0">
                <span className="block font-bold text-gray-900 leading-snug">After Class 10: Study, Skills &amp; Career Clarity</span>
                <span className="block text-xs text-gray-600 mt-0.5 leading-relaxed">Routes, subject families, skilled work, money checks, your state&rsquo;s official links and an interest-exploration quiz.</span>
              </span>
            </span>
            <ArrowRight size={18} className="text-gray-400 shrink-0 group-hover:text-amber-600 group-hover:translate-x-0.5 transition-all"/>
          </Link>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
            {groups.map((g, i) => {
              const v = GROUP_VISUALS[g.group] || GROUP_VISUALS.MPC;
              const Icon = v.Icon;
              const a = attempts[g.group] || { attempts_used: 0, attempts_remaining: maxAttempts };
              const locked = !isAdmin && a.attempts_remaining === 0;
              return (
                <motion.div
                  key={g.group}
                  initial={{ opacity: 0, y: 16 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: i * 0.08, duration: 0.5, ease: 'easeOut' }}
                  whileHover={{ y: -4 }}
                >
                  <Link
                    to={`/career-clarity/after-10th/${g.group}`}
                    data-testid={`after10th-group-${g.group}`}
                    className={`block group relative bg-gradient-to-br ${v.soft} rounded-3xl border border-white/60 shadow-md hover:shadow-2xl ${v.glow} transition-all duration-300 overflow-hidden`}
                  >
                    {/* Decorative gradient blob */}
                    <div className={`absolute -top-12 -right-12 w-36 h-36 rounded-full bg-gradient-to-br ${v.gradient} opacity-15 blur-xl group-hover:opacity-25 transition-opacity`}/>

                    <div className="p-6 relative">
                      <div className="flex items-center justify-between mb-5">
                        <span className={`inline-flex w-12 h-12 rounded-2xl bg-gradient-to-br ${v.gradient} items-center justify-center text-white shadow-lg group-hover:scale-110 group-hover:rotate-3 transition-transform`}>
                          <Icon size={22}/>
                        </span>
                        <span className={`inline-flex px-2.5 py-1 rounded-full ${v.chip} text-[10px] font-extrabold uppercase tracking-widest`} data-testid={`after10th-attempts-${g.group}`}>
                          {isAdmin ? 'Admin · unlimited' : (locked ? 'No attempts left' : `${a.attempts_remaining} of ${maxAttempts} left`)}
                        </span>
                      </div>
                      <h3 className="text-3xl font-black text-gray-900 leading-none">{g.group}</h3>
                      <p className="text-sm font-bold text-gray-700 mt-1.5">{g.group_full_form}</p>
                      <p className="text-sm text-gray-600 mt-4 leading-relaxed line-clamp-3">{g.purpose}</p>
                      <div className="mt-6 pt-4 border-t border-white/80 flex items-center justify-between">
                        <span className="text-[11px] font-bold uppercase tracking-widest text-gray-500">{g.total_questions}-question fit quiz</span>
                        <span className={`inline-flex items-center gap-1 text-sm font-bold bg-gradient-to-r ${v.gradient} bg-clip-text text-transparent group-hover:gap-2 transition-all`}>
                          {locked ? 'View report' : 'Take the quiz'} <ArrowRight size={14} className="text-gray-700 group-hover:translate-x-0.5 transition-transform"/>
                        </span>
                      </div>
                    </div>
                  </Link>
                </motion.div>
              );
            })}
          </div>

          <p className="text-center text-xs text-gray-500 mt-12" data-testid="after10th-foot">
            Each quiz is self-contained — you don&apos;t have to take all five.
            One question at a time · honest results · English only.
          </p>
        </div>
      </div>
    </div>
  );
}
